import {
  showToast,
  closeToast as closeToastById,
  Toast,
  ToastTitle,
  ToastDescription,
} from '@/components/gluestack/toast';
import { VStack } from '@/components/gluestack/vstack';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { HStack } from '@/components/gluestack/hstack';
import { Link, LinkText } from '@/components/gluestack/link';
import { Platform, ViewStyle } from 'react-native';
import { Spinner } from '@/components/gluestack/spinner';
import { usePaywallError } from './usePaywallError';
import { Text } from '@/components/gluestack/text';
import { GestureDetector, Gesture, GestureHandlerRootView } from 'react-native-gesture-handler';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { StartUp02Icon } from '@hugeicons-pro/core-solid-rounded';

export interface IToastAction {
  text: string;
  onPress: () => void;
}

type ToastPlacement = 'top' | 'bottom';

interface IDismissibleToastProps {
  children: ReactNode;
  onDismiss: () => void;
  placement: ToastPlacement;
}

function DismissibleToast({ children, onDismiss, placement }: IDismissibleToastProps) {
  const translateY = useSharedValue(0);
  const opacity = useSharedValue(1);

  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const placementShared = useSharedValue(placement === 'top' ? 1 : 0);
  useEffect(() => {
    placementShared.value = placement === 'top' ? 1 : 0;
  }, [placement]);

  const callDismiss = () => onDismissRef.current?.();

  const panGesture = useMemo(
    () =>
      Gesture.Pan()
        .onUpdate((event) => {
          'worklet';
          if (placementShared.value === 1) {
            translateY.value = Math.min(0, event.translationY);
          } else {
            translateY.value = Math.max(0, event.translationY);
          }
        })
        .onEnd((event) => {
          'worklet';
          const threshold = -50;
          const isTop = placementShared.value === 1;
          const shouldDismiss = isTop
            ? event.translationY < threshold
            : event.translationY > -threshold;

          if (shouldDismiss) {
            const dismissDistance = isTop ? -100 : 100;
            translateY.value = withTiming(dismissDistance, { duration: 200 });
            opacity.value = withTiming(0, { duration: 200 }, () => {
              scheduleOnRN(callDismiss);
            });
          } else {
            translateY.value = withSpring(0, { damping: 20, stiffness: 200 });
            opacity.value = withSpring(1, { damping: 20, stiffness: 200 });
          }
        }),
    []
  );

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
    opacity: opacity.value,
  }));

  const gestureContent = (
    <GestureDetector gesture={panGesture}>
      <Animated.View style={animatedStyle}>{children}</Animated.View>
    </GestureDetector>
  );

  return Platform.OS === 'web' ? (
    gestureContent
  ) : (
    <GestureHandlerRootView style={{ flex: 0, width: '100%' }}>
      {gestureContent}
    </GestureHandlerRootView>
  );
}

/**
 * Lifts bottom toasts above the tab bar (small screens) or the bottom edge
 * (large screens). Lives in the toast content so only visible toasts read
 * the screen size.
 */
function useToastContainerStyle(placement: ToastPlacement): ViewStyle | undefined {
  const isLargeScreen = useIsLargeScreen();
  return placement === 'bottom'
    ? { transform: [{ translateY: isLargeScreen ? -16 : -64 }] }
    : undefined;
}

interface ISuccessToastProps {
  id: string;
  message: string;
  action?: IToastAction;
  placement: ToastPlacement;
}

function SuccessToast({ id, message, action, placement }: ISuccessToastProps) {
  const { t } = useTranslation();
  const containerStyle = useToastContainerStyle(placement);

  return (
    <DismissibleToast onDismiss={() => closeToastById(id)} placement={placement}>
      <Toast nativeID={'toast-' + id} action="secondary" variant="solid" style={containerStyle}>
        <VStack space="xs">
          <ToastDescription>{message}</ToastDescription>
          {action && (
            <Link
              onPress={() => {
                action.onPress();
                closeToastById(id);
              }}>
              <LinkText className="font-bold text-typography-50">
                {action.text || t('common.undo')}
              </LinkText>
            </Link>
          )}
        </VStack>
      </Toast>
    </DismissibleToast>
  );
}

interface IErrorToastProps {
  id: string;
  message: string;
  error?: any;
  placement: ToastPlacement;
}

function ErrorToast({ id, message, error, placement }: IErrorToastProps) {
  const { t } = useTranslation();
  const { checkForPaywallError, navigateToPlans } = usePaywallError();
  const containerStyle = useToastContainerStyle(placement);

  const paywallCheck = error ? checkForPaywallError(error) : { isPaywall: false as const };

  let content: ReactNode;
  if (paywallCheck.isPaywall) {
    content = (
      <VStack space="xs" className="gap-2">
        <ToastTitle className="font-semibold">{t('common.error.general')}</ToastTitle>
        <ToastDescription>{paywallCheck.message}</ToastDescription>
        {paywallCheck.resetTimeMessage && (
          <Text className="text-xs text-typography-200">{paywallCheck.resetTimeMessage}</Text>
        )}
        <ButtonWrapper
          icon={StartUp02Icon}
          size="md"
          text={t('upgrade.upgrade_button')}
          className="mt-1 self-start"
          onPress={() => {
            navigateToPlans();
            closeToastById(id);
          }}
        />
      </VStack>
    );
  } else if (error?.status === 403) {
    content = (
      <VStack space="xs">
        <ToastTitle className="font-semibold">{t('common.error.general')}</ToastTitle>
        <ToastDescription>{t('common.error.ipBlocked')}</ToastDescription>
      </VStack>
    );
  } else {
    const errorMessage = error ? `${message} (${error.message || String(error)})` : message;
    content = (
      <VStack space="xs">
        <ToastTitle className="font-semibold">{t('common.error.general')}</ToastTitle>
        <ToastDescription>{errorMessage}</ToastDescription>
      </VStack>
    );
  }

  return (
    <DismissibleToast onDismiss={() => closeToastById(id)} placement={placement}>
      <Toast nativeID={'toast-' + id} action="error" variant="solid" style={containerStyle}>
        {content}
      </Toast>
    </DismissibleToast>
  );
}

interface ILoadingToastProps {
  id: string;
  message: string;
  placement: ToastPlacement;
}

function LoadingToast({ id, message, placement }: ILoadingToastProps) {
  const containerStyle = useToastContainerStyle(placement);

  return (
    <DismissibleToast onDismiss={() => closeToastById(id)} placement={placement}>
      <Toast nativeID={'toast-' + id} action="warning" variant="solid" style={containerStyle}>
        <HStack className="items-center gap-2">
          <Spinner size="small" className="mr-1" color="black" />
          <ToastDescription>{message}</ToastDescription>
        </HStack>
      </Toast>
    </DismissibleToast>
  );
}

/**
 * Displays a success toast with the given message.
 * @param message - The success message to display in the toast description.
 * @param action - Optional action to display in the toast.
 * @param placement - The position where the toast should appear ('top' or 'bottom'). Defaults to 'top'.
 */
const showSuccessToast = (
  message: string,
  action?: IToastAction,
  placement: ToastPlacement = 'top'
) => {
  return showToast({
    placement,
    duration: action ? 6000 : 3000,
    render: ({ id }): ReactNode => (
      <SuccessToast id={id} message={message} action={action} placement={placement} />
    ),
  });
};

/**
 * Displays an error toast with the given message.
 * @param message - The error message to display in the toast description.
 * @param error - Optional error object that will be appended to the message.
 * @param placement - The position where the toast should appear ('top' or 'bottom'). Defaults to 'top'.
 */
const showErrorToast = (message: string, error?: any, placement: ToastPlacement = 'top') => {
  // Paywall and IP-blocked errors (both 403s) stay on screen longer.
  const duration = error?.status === 403 ? 10000 : 5000;
  return showToast({
    placement,
    duration,
    render: ({ id }): ReactNode => (
      <ErrorToast id={id} message={message} error={error} placement={placement} />
    ),
  });
};

/**
 * Displays a persistent loading toast with a spinner. Returns a function to close it.
 * @param message - The loading message to display
 * @param placement - 'top' or 'bottom'
 * @returns A function that, when called, closes the toast
 */
const showLoadingToast = (message: string, placement: ToastPlacement = 'bottom') => {
  const id = showToast({
    placement,
    duration: null,
    render: ({ id: toastId }): ReactNode => (
      <LoadingToast id={toastId} message={message} placement={placement} />
    ),
  });
  return () => closeToastById(id);
};

const closeToast = (id: string) => {
  closeToastById(id);
};

const toastMessages = {
  showSuccessToast,
  showErrorToast,
  showLoadingToast,
  closeToast,
};

/**
 * Custom hook to display toast messages.
 * Provides functions to show standardized success and error toast messages.
 * The returned functions are stable module-level bindings, so calling this
 * hook subscribes the component to nothing — paywall/auth, screen size, and
 * translation concerns live inside the toast content components and only run
 * while a toast is visible.
 * @returns An object containing the showSuccessToast and showErrorToast functions.
 */
export function useToastMessages() {
  return toastMessages;
}
